import React, { useEffect, useState } from "react";
import { DialogWrapMedium } from "../../../controls/components/dialogWrap/DialogWrap";
import { UserFormServiceButtonClose, UserFormServiceButtonCreated } from "../../MyBooking/styled/DesStyled";
import useBanPartnerAdmin from "../hook/BanPartnerAdmin";

/**
 * Khoá trạm — hai bước.
 *
 * Bước thường: nhập lý do, bấm khoá.
 * Nếu backend chặn vì trạm còn khách chưa tới ngày lấy đồ (Quân chốt 13/08),
 * hộp thoại mở thêm **bước khoá khẩn**: hiện rõ số đơn đang bị ảnh hưởng, đòi
 * một lý do riêng dài hơn, rồi mới cho khoá.
 *
 * Cố ý KHÔNG gộp hai bước làm một: người bấm phải nhìn thấy con số khách bị ảnh
 * hưởng trước khi quyết, chứ không phải tick sẵn một ô "bỏ qua kiểm tra".
 */
export default function DialogBanPartner(props: {
	openDialog: boolean;
	setOpenDialog: (v: boolean) => void;
	partnerId: string | null;
	reload: () => void;
}) {
	const [reason, setReason] = useState("");
	const [forceReason, setForceReason] = useState("");
	const [blockedCount, setBlockedCount] = useState<number | null>(null);
	const [isBlocked, setIsBlocked] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const { banPartnerAd, MIN_BAN_REASON, MIN_FORCE_BAN_REASON } = useBanPartnerAdmin({
		reload: props.reload,
	});

	useEffect(() => {
		if (props.openDialog) {
			setReason("");
			setForceReason("");
			setBlockedCount(null);
			setIsBlocked(false);
			setSubmitting(false);
		}
	}, [props.openDialog]);

	const handleConfirm = async (force: boolean) => {
		if (!props.partnerId) return;
		setSubmitting(true);
		try {
			const result = await banPartnerAd(props.partnerId, reason, force, forceReason);
			if (result.ok) {
				props.setOpenDialog(false);
				return;
			}
			if (!result.ok && result.blockedByLuggage) {
				setIsBlocked(true);
				setBlockedCount(result.ordersInStorage);
			}
		} finally {
			setSubmitting(false);
		}
	};

	const countLabel = blockedCount == null ? "một số" : String(blockedCount);
	const canForce = forceReason.trim().length >= MIN_FORCE_BAN_REASON && !submitting;

	return (
		<DialogWrapMedium
			login={true}
			open={props.openDialog}
			onClose={() => props.setOpenDialog(false)}
			title={isBlocked ? "Không khoá được — còn khách đang gửi đồ" : "Ban station"}
			actions={
				<>
					<UserFormServiceButtonClose
						type="button"
						onClick={() => props.setOpenDialog(false)}>
						{isBlocked ? "Để sau" : "Cancel"}
					</UserFormServiceButtonClose>
					{isBlocked ? (
						<UserFormServiceButtonCreated
							type="button"
							disabled={!canForce}
							style={
								canForce
									? { background: "#b71c1c", borderColor: "#b71c1c" }
									: { opacity: 0.5, cursor: "not-allowed" }
							}
							onClick={() => void handleConfirm(true)}>
							{submitting ? "Đang khoá…" : "Vẫn khoá khẩn cấp"}
						</UserFormServiceButtonCreated>
					) : (
						<UserFormServiceButtonCreated
							type="button"
							disabled={submitting}
							onClick={() => void handleConfirm(false)}>
							{submitting ? "Đang khoá…" : "Confirm ban"}
						</UserFormServiceButtonCreated>
					)}
				</>
			}>
			<div>
				{isBlocked ? (
					<>
						<div
							style={{
								padding: "12px 14px",
								marginBottom: 14,
								background: "#fdecea",
								border: "1px solid #f5c6c2",
								borderRadius: 8,
								fontSize: 13,
								lineHeight: 1.65,
								color: "#8a1c13",
							}}>
							Trạm này còn <strong>{countLabel} đơn</strong> khách chưa tới ngày lấy đồ. Khoá bây giờ thì
							khách tới nơi sẽ thấy điểm đã đóng — có thể mất đồ thật.
							<div style={{ marginTop: 8 }}>
								<strong>Nên làm:</strong> chuyển trạm sang <em>ngừng nhận đơn mới</em> ngay, rồi khoá
								sau khi đơn cuối qua ngày lấy.
							</div>
						</div>

						<p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.6 }}>
							Chỉ khoá khẩn cấp khi <strong>chính trạm mới là mối nguy</strong> (ôm đồ khách, lừa đảo) —
							lúc đó để nó chạy tiếp còn tệ hơn. Lần khoá này sẽ được ghi nhật ký kèm tên bạn, lý do dưới
							đây, và số đơn đã bỏ qua.
						</p>

						<label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
							Lý do khoá khẩn cấp (tối thiểu {MIN_FORCE_BAN_REASON} ký tự)
						</label>
						<textarea
							style={{ width: "100%", minHeight: 90, padding: 8, fontSize: 13, boxSizing: "border-box" }}
							placeholder="VD: Trạm ôm đồ khách không trả, đã có 2 khách báo mất đồ ngày 12/08"
							value={forceReason}
							onChange={(e) => setForceReason(e.target.value)}
						/>
					</>
				) : (
					<>
						<p style={{ marginBottom: 8, fontSize: 13 }}>
							Minimum <strong>{MIN_BAN_REASON}</strong> characters. This blocks the station portal and
							APIs for this partner account.
						</p>
						<textarea
							style={{ width: "100%", minHeight: 100, padding: 8, fontSize: 13, boxSizing: "border-box" }}
							placeholder="Reason for ban…"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>
					</>
				)}
			</div>
		</DialogWrapMedium>
	);
}
